<template>
  <div class="performance-dashboard">
    <performance-skeleton v-if="isLoading" />
    <div v-else>
      <!-- Top Metrics Cards -->
      <b-row class="match-height mb-2">
        <b-col
          v-for="metric in metrics"
          :key="metric.key"
          lg="3"
          sm="6"
        >
          <b-card class="metric-card">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="d-flex align-items-baseline">
                  <h2 class="font-weight-bolder">
                    {{ teamPerformance?.[metric.key] }}
                  </h2>
                  <p
                    v-if="metric.description"
                    class="text-muted small mb-0"
                    style="margin-left: 8px;"
                  >
                    {{ metric.description }}
                  </p>
                </div>
                <p class="text-muted mb-0">
                  {{ metric.label }}
                </p>
              </div>
              <b-avatar
                :variant="metric.variant"
                size="45"
              >
                <feather-icon
                  :icon="metric.icon"
                  size="21"
                />
              </b-avatar>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Middle Section: Charts and Indicators -->
      <b-row class="match-height mb-2">
        <!-- Completion Rate Chart -->
        <b-col
          lg="4"
          md="6"
        >
          <b-card>
            <h5 class="mb-2">
              {{ $t('Completion Rate') }}
            </h5>
            <div class="text-center">
              <vue-apex-charts
                type="donut"
                height="280"
                :options="completionRateOptions"
                :series="[parseFloat((teamPerformance?.completionRate || 0).toFixed(2)), parseFloat((100 - (teamPerformance?.completionRate || 0)).toFixed(2))]"
              />
              <h3 class="font-weight-bold text-primary">
                {{ (teamPerformance?.completionRate || 0).toFixed(2) }}%
              </h3>
              <p class="text-muted">
                {{ $t('complete') }}
              </p>
            </div>
          </b-card>
        </b-col>

        <!-- Tasks by Status Chart -->
        <b-col
          lg="4"
          md="6"
        >
          <b-card>
            <h5 class="mb-2">
              {{ $t('Tasks by Status') }}
            </h5>
            <div
              v-if="tasksByStatusData.length > 0"
              class="text-center"
            >
              <vue-apex-charts
                type="donut"
                height="280"
                :options="tasksByStatusOptions"
                :series="tasksByStatusSeries"
              />
              <div class="mt-2">
                <div
                  v-for="(item, index) in tasksByStatusData"
                  :key="index"
                  class="d-flex justify-content-between align-items-center mb-1"
                >
                  <div class="d-flex align-items-center">
                    <span
                      class="bullet bullet-sm mr-50"
                      :style="{ backgroundColor: tasksByStatusColors[index] }"
                    />
                    <span class="text-muted">
                      {{ item.category }}
                      ({{ item.count }})
                    </span>
                  </div>
                  <span class="text-muted">{{ item.percentage.toFixed(2) }}%</span>
                </div>
              </div>
            </div>
            <div
              v-else
              class="d-flex align-items-center justify-content-center"
              style="min-height: 280px;"
            >
              <p class="text-muted mb-0">
                {{ $t('No data to display') }}
              </p>
            </div>
          </b-card>
        </b-col>

        <!-- Team Metrics -->
        <b-col
          lg="4"
          md="12"
        >
          <b-card>
            <h5 class="mb-2">
              {{ $t('Team Metrics') }}
            </h5>

            <div class="mb-3">
              <div class="d-flex justify-content-between mb-50">
                <span>{{ $t('Team Velocity') }}</span>
                <span class="font-weight-bold">{{ (teamPerformance?.velocity || 0).toFixed(1) }}</span>
              </div>
              <p class="small text-muted mb-0">
                {{ $t('Tasks completed per week') }}
              </p>
            </div>

            <div class="mb-3">
              <div class="d-flex justify-content-between mb-50">
                <span>{{ $t('Completion Rate') }}</span>
                <span class="font-weight-bold">{{ (teamPerformance?.completionRate || 0).toFixed(2) }}%</span>
              </div>
              <b-progress
                :value="teamPerformance?.completionRate || 0"
                :max="100"
                height="8px"
                variant="success"
                class="mb-1"
              />
            </div>

            <div class="mb-2">
              <div class="d-flex justify-content-between mb-50">
                <span>{{ $t('Overall Performance') }}</span>
                <div>
                  <feather-icon
                    v-for="i in 5"
                    :key="i"
                    icon="CircleIcon"
                    size="14"
                    :class="i <= overallRating ? 'text-success' : 'text-muted'"
                    class="fill-current"
                  />
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Bottom Section: Trend Charts -->
      <b-row class="match-height">
        <!-- Monthly Task Trend -->
        <b-col
          lg="8"
          md="12"
        >
          <b-card>
            <h5 class="mb-2">
              {{ $t('Monthly Task Trend') }}
            </h5>
            <vue-apex-charts
              type="line"
              height="300"
              :options="monthlyTrendChartOptions"
              :series="monthlyTrendSeries"
            />
          </b-card>
        </b-col>

        <!-- Top Active Users per Month -->
        <b-col
          lg="4"
          md="12"
        >
          <b-card class="h-100">
            <h5 class="mb-2">
              {{ $t('Top Active Users') }}
            </h5>
            <div
              v-if="topActiveUsersByMonth.length > 0"
              class="top-active-users-list"
            >
              <div
                v-for="(point, index) in topActiveUsersByMonth"
                :key="index"
                class="d-flex align-items-center justify-content-between mb-1 py-75 px-1 rounded top-active-user-row"
              >
                <div class="d-flex align-items-center">
                  <b-avatar
                    :src="getStorage(point.topActiveUser.avatar)"
                    :text="getInitials(point.topActiveUser.name)"
                    size="36"
                    variant="light-primary"
                    class="mr-1"
                  />
                  <div>
                    <p class="font-weight-bold mb-0 text-body">
                      {{ point.topActiveUser.name }}
                    </p>
                    <small class="text-muted">{{ point.monthLabel }}</small>
                  </div>
                </div>
                <b-badge
                  variant="light-primary"
                  class="ml-50"
                >
                  {{ point.topActiveUserActivityCount }}
                </b-badge>
              </div>
            </div>
            <div
              v-else
              class="d-flex align-items-center justify-content-center"
              style="min-height: 280px;"
            >
              <p class="text-muted mb-0">
                {{ $t('No data to display') }}
              </p>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  BCard,
  BRow,
  BCol,
  BAvatar,
  BProgress,
  BBadge,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import PerformanceSkeleton from './PerformanceSkeleton.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BAvatar,
    BProgress,
    BBadge,
    VueApexCharts,
    PerformanceSkeleton,
  },
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      metrics: [
        {
          key: 'totalTasksCreated', label: this.$t('Tasks Created'), icon: 'PlusCircleIcon', variant: 'light-primary',
        },
        {
          key: 'totalTasksInProgress', label: this.$t('Tasks In Progress'), icon: 'ActivityIcon', variant: 'light-warning',
        },
        {
          key: 'totalTasksCompleted', label: this.$t('Tasks Completed'), icon: 'CheckCircleIcon', variant: 'light-success',
        },
        {
          key: 'averageCompletionTime', label: this.$t('Completion Time'), description: this.$t('days / task'), icon: 'ClockIcon', variant: 'light-wz-teal',
        },
      ],
      completionRateOptions: {
        chart: {
          type: 'donut',
        },
        colors: ['#00d4bd', '#2d3436'],
        labels: ['Complete', 'Incomplete'],
        legend: {
          show: false,
        },
        plotOptions: {
          pie: {
            donut: {
              size: '75%',
              labels: {
                show: true,
                name: {
                  show: false,
                },
                value: {
                  show: true,
                  fontSize: '2rem',
                  fontWeight: 600,
                  color: '#00d4bd',
                  formatter: val => `${parseFloat(val).toFixed(2)}%`,
                },
                total: {
                  show: true,
                  label: 'Complete',
                  fontSize: '1rem',
                  color: '#6e6b7b',
                  formatter: () => '',
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 0,
        },
      },
      monthlyTrendOptions: {
        chart: {
          type: 'line',
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
        },
        stroke: {
          curve: 'smooth',
          width: 3,
        },
        colors: ['#7367f0', '#00d4bd'],
        xaxis: {
          categories: [],
          labels: {
            style: {
              colors: '#6e6b7b',
            },
          },
        },
        yaxis: {
          min: 0,
          tickAmount: 4,
          labels: {
            style: {
              colors: '#6e6b7b',
            },
          },
        },
        grid: {
          borderColor: '#e7e7e7',
          strokeDashArray: 5,
        },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'left',
        },
        tooltip: {
          theme: 'dark',
        },
      },
    }
  },
  computed: {
    ...mapGetters('boards', {
      board: 'GET_CURRENT_BOARD',
      teamPerformance: 'GET_CURRENT_BOARD_TEAM_PERFORMANCE',
      boardPerformance: 'GET_CURRENT_BOARD_PERFORMANCE',
      isLoading: 'GET_LOADING',
    }),
    projectUid() {
      return this.$store.state.project.selectedProject
    },
    boardUid() {
      return this.base58Converter(this.$route.params.boardUid, false)
    },
    tasksByStatusSeries() {
      if (!this.teamPerformance?.tasksByStatus) return [0, 0, 0]

      const data = this.teamPerformance.tasksByStatus
      return data.map(item => item.count)
    },
    tasksByStatusData() {
      if (!this.teamPerformance?.tasksByStatus) return []
      return this.teamPerformance.tasksByStatus
    },
    tasksByStatusColors() {
      if (!this.teamPerformance?.tasksByStatus) return []
      return this.teamPerformance.tasksByStatus.map(item => item.color || '#00d4bd')
    },
    tasksByStatusLabels() {
      if (!this.teamPerformance?.tasksByStatus) return []
      return this.teamPerformance.tasksByStatus.map(item => item.category || '')
    },
    tasksByStatusOptions() {
      return {
        chart: {
          type: 'donut',
        },
        colors: this.tasksByStatusColors,
        labels: this.tasksByStatusLabels,
        legend: {
          show: false,
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 0,
        },
      }
    },
    topActiveUsersByMonth() {
      const data = this.boardPerformance || []
      return data.filter(point => point.topActiveUser)
    },
    monthlyTrendSeries() {
      const data = this.boardPerformance || []
      return [
        {
          name: this.$t('Tasks Created'),
          data: data.map(point => point.tasksCreated || 0),
        },
        {
          name: this.$t('Tasks Completed'),
          data: data.map(point => point.tasksCompleted || 0),
        },
      ]
    },
    monthlyTrendChartOptions() {
      const data = this.boardPerformance || []
      return {
        ...this.monthlyTrendOptions,
        xaxis: {
          ...this.monthlyTrendOptions.xaxis,
          categories: data.map(point => point.monthLabel || point.month),
        },
      }
    },
    overallRating() {
      if (!this.teamPerformance) return 0

      // Calculate rating based on completion rate and velocity
      const completionScore = this.teamPerformance.completionRate || 0
      const velocityScore = Math.min(100, (this.teamPerformance.velocity || 0) * 10)
      const score = (completionScore + velocityScore) / 2
      return Math.round(score / 20) // Convert to 1-5 scale
    },
  },
  watch: {
    timeRange: {
      handler(val) {
        if (val.preset !== 'CUSTOM' || (val.customStart && val.customEnd)) {
          this.fetchTeamPerformance({
            projectUid: this.projectUid,
            boardUid: this.boardUid,
            timeRange: val,
          })
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchTeamAndBoardPerformance({
      projectUid: this.projectUid,
      boardUid: this.boardUid,
      timeRange: this.timeRange,
    })
  },
  beforeDestroy() {
    this.clearPerformanceStats()
  },
  methods: {
    ...mapActions('boards', ['fetchTeamPerformance', 'fetchTeamAndBoardPerformance', 'clearPerformanceStats']),
    formatCompletionTime(days) {
      if (!days) return '0d'
      return `${days.toFixed(1)}d`
    },
    maxDate() {
      return new Date()
    },
  },
}
</script>

<style lang="scss" scoped>
.performance-dashboard {
  .metric-card {
    transition: transform 0.2s;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    }
  }

  .bullet {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .fill-current {
    fill: currentColor;
  }

  .top-active-user-row {
    transition: background 0.15s;
    &:hover {
      background: rgba(115, 103, 240, 0.06);
    }
  }
}

.dark-layout {
  .performance-dashboard {
    .text-muted {
      color: #b4b7bd !important;
    }
  }
}
</style>
